<!-- EntityGroups.vue -->
<template>
  <Stack v-if="isEditable">
    <div>
      <Title size="5">Add Relationships</Title>
      <Subtitle>
        Create groups of accounts, people, and households
      </Subtitle>
    </div>

    <!-- Parent Entity -->
    <Field>
      <Label>{{ hasParent ? 'Update Group' : 'Add to Group' }}</Label>
      <div class="field is-grouped mb-0">
        <div class="control is-expanded">
          <EntitySelect
            :default-entities="potentialParents"
            :current="currentParent"
            :disable-api-calls="true"
            @select="handleParentSelect"
            :placeholder="hasParent ? 'Search to replace current Group' : 'Search for a Group'"
          />
        </div>
        <div class="control">
          <Button
            variant="primary"
            size="3_5"
            :disabled="!selectedParentId || loading"
            @click="addRelationship('parent')"
          >
            {{ hasParent ? 'Update Group' : 'Add to Group' }}
          </Button>
        </div>
        <div v-if="hasParent" class="control">
          <Button
            variant="danger"
            size="3_5"
            :disabled="loading || hasRemovedFromGroup"
            @click="removeFromGroup"
          >
            Remove from Group
          </Button>
        </div>
      </div>
      <div class="control">
        <EntityForm
          :callback="handleEntityCreated"
          relationshipType="Parent"
          label="Group"
          :entity-external-id="entityExternalId"
        >
          Create New Group
        </EntityForm>
      </div>
    </Field>

    <!-- Add Child Entity -->
    <Field>
      <Label>Add Member</Label>
      <div class="field is-grouped mb-0">
        <div class="control is-expanded">
          <EntitySelect
            :default-entities="potentialChildren"
            :disable-api-calls="true"
            @select="handleChildSelect"
            placeholder="Search for a Member"
          />
        </div>
        <div class="control">
          <Button
            variant="primary"
            size="3_5"
            :disabled="!selectedChildId || loading"
            @click="addRelationship('child')"
          >
            Add Member
          </Button>
        </div>
      </div>
      <div class="control">
        <EntityForm
          :callback="handleEntityCreated"
          relationshipType="Child"
          label="Member"
          :entity-external-id="entityExternalId"
        >
          Create New Member
        </EntityForm>
      </div>
    </Field>
  </Stack>
</template>

<script>
import EntitySelect from '@frontend/ui/components/utils/EntitySelect.vue';
import EntityForm from './EntityForm.vue';
import * as Sentry from '@sentry/vue';
import { Button, Field, Subtitle, Title, Stack, Label } from '@stakeui';

import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

export default {
  name: 'EntityGroups',
  components: {
    EntitySelect,
    EntityForm,
    Button,
    Field,
    Label,
    Stack,
    Subtitle,
    Title,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    currentParent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      potentialParents: [],
      potentialChildren: [],
      parentExternalId: null,
      hasRemovedFromGroup: null,
      selectedParentId: '',
      selectedChildId: '',
      loading: false,
      isEditable: false,
      currentUserPermissions: [],
      errorMessage: null,
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),

    hasParent() {
      return this.currentParent !== null;
    },
  },
  created() {
    if (this.advisorStore.hasFeature('Teams')) {
      this.fetchPermissionsTeams();
    } else {
      this.fetchPermissions();
    }
    this.loadRelationshipOptions();
  },
  methods: {
    // New version using the teams sharing system
    async fetchPermissionsTeams() {
      this.currentUserPermissions = [];

      try {
        const response = await fetch(`/api/v1/can/edit/entity/${this.entityExternalId}/`);
        this.isEditable = response.ok;

        if(!response.ok && response.status !== 403) {
          throw new Error('Failed to check permission');
        }

      } catch (error) {
        this.isEditable = false;
        
        this.$buefy.toast.open({
          message: 'Unable to check edit permission for this entity',
          type: 'is-danger',
        });
        Sentry.captureMessage(`Unable to check edit permission for entity ${this.entityExternalId}`, { level: 'warning' });
      }
    },
    // Legacy version
    async fetchPermissions() {
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/permissions/`);
        if (!response.ok) {
          this.isEditable = false;
          this.currentUserPermissions = [];
          this.$buefy.toast.open({
            message: 'Failed to load relationship options',
            type: 'is-danger',
          });
          Sentry.captureMessage('Fetch Permissions response not ok in EntityGroups', { level: 'warning' });
          return;
        }
        
        const data = await response.json();
        this.currentUserPermissions = data.current_user_permissions.permissions || [];
        this.isEditable = this.currentUserPermissions.includes('change_entity');
      } catch (error) {
        this.isEditable = false;
        this.currentUserPermissions = [];
        this.$buefy.toast.open({
          message: 'Failed to fetch permissions',
          type: 'is-danger',
        });
        Sentry.captureMessage('Error fetching permissions in EntityGroups', { level: 'warning' });
      }
    },
    async loadRelationshipOptions() {
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/relationship-options/`);
        if (!response.ok) throw new Error('Failed to load relationship options');
        const data = await response.json();
        this.potentialParents = data.potential_parents || [];
        this.potentialChildren = data.potential_children || [];
        this.parentExternalId = data.parent_entities[0]?.external_id ?? null;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load relationship options',
          type: 'is-danger',
        });
        Sentry.captureMessage('Error loading relationship options in EntityGroups', { level: 'warning' });
      }
    },
    handleParentSelect(entity) {
      this.selectedParentId = entity.external_id;
    },
    handleChildSelect(entity) {
      this.selectedChildId = entity.external_id;
    },
    handleEntityCreated(entityData) {
      console.log('Entity created:', entityData);
      this.$buefy.toast.open({
        message: `Successfully created ${entityData.relationshipType}`,
        type: 'is-success',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    async addRelationship(type) {
      if (this.loading) return;
      this.loading = true;

      try {
        const relatedEntityId = type === 'parent' ? this.selectedParentId : this.selectedChildId;
        
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/relationships/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            relationship_type: type,
            related_entity_id: relatedEntityId
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to add relationship');
        }

        // Reset selection
        if (type === 'parent') {
          this.selectedParentId = '';
        } else {
          this.selectedChildId = '';
        }

        this.$buefy.toast.open({
          message: `Successfully added ${type} entity, refreshing page now`,
          type: 'is-success'
        });
        
        // Add slight delay to show the toast before reload
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to add entity',
          type: 'is-danger'
        });
        Sentry.captureMessage('Error adding relationship in EntityGroups', { level: 'warning' });
      } finally {
        this.loading = false;
      }
    },
    async removeFromGroup() {
      if (this.loading || !this.parentExternalId) return;
      this.loading = true;

      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/relationships/remove/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            relationship_type: 'parent',
            related_entity_id: this.parentExternalId,
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to remove relationship');
        }

        this.$buefy.toast.open({
          message: `Successfully removed from group, refreshing page now`,
          type: 'is-success'
        });
        this.hasRemovedFromGroup = true;
        
        // Add slight delay to show the toast before reload
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to remove from group',
          type: 'is-danger'
        });
        Sentry.captureMessage('Error removing entity from group in EntityGroups', { level: 'warning' });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.field.is-grouped .control.is-expanded {
  flex-grow: 1;
}
</style>
